
import React, { useRef, useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { MexicoMapProps, MexicoMapRef, MapMarkerRefs } from './types';
import { useLanguage } from '../../contexts/language';
import MapMarkers from './MapMarkers';
import MapLegend from './MapLegend';
import { chiapas_cities } from '../../data/chiapas/cities-data';
import { hidalgo_cities } from '../../data/hidalgo/cities-data';
import { useMapInitialization } from '../../hooks/use-map-initialization';
import { useMapEvents } from '../../hooks/use-map-events';
import { useMapNavigation } from '../../hooks/use-map-navigation';
import { useIsMobile } from '../../hooks/use-mobile';
import MapContainer from './MapContainer';
import { useToast } from '@/hooks/use-toast';
import { formatCurrencyShort } from '../../data/utils/money-utils';
import { useMexicoMapHandles } from './useMexicoMapHandles';
import { useMexicoMapSelection } from './useMexicoMapSelection';
import { LegendFilterProvider } from '../../contexts/legend-filter/LegendFilterContext';
import { addVectorLayers, waitForStyleLoad, verifyMapboxSetup } from './region-layer/utils/vector-layers';

const MexicoMap = forwardRef<MexicoMapRef, MexicoMapProps>((props, ref) => {
  const { regions, onRegionClick, filteredRegionIds, selectedRegion, selectedCity, onCityClick, wasInMexicoView } = props;
  const mapContainer = useRef<HTMLDivElement>(null);
  const { t } = useLanguage();
  const [showChiapasCities, setShowChiapasCities] = useState(false);
  const [showHidalgoCities, setShowHidalgoCities] = useState(false);
  const markerRefs = useRef<MapMarkerRefs>({
    regions: [],
    cities: [],
    churches: [],
    labels: []
  });

  const { isMobile } = useIsMobile();
  const { toast } = useToast();
  const toastShownRef = useRef({ chiapas: false, hidalgo: false });
  const [is3DMode, setIs3DMode] = useState(false);
  const [showGlobalRegions, setShowGlobalRegions] = useState(true);
  const [isMexicoView, setIsMexicoView] = useState(false);
  const [layersAdded, setLayersAdded] = useState(false);
  const styleElementRef = useRef<HTMLStyleElement | null>(null);

  const { map, mapLoaded, mapZoomLevel, setMapZoomLevel } = useMapInitialization({ 
    mapContainer, 
    selectedRegion, 
    selectedCity 
  });

  useMapEvents(mapContainer, map, mapZoomLevel);

  const onChiapasEnterCallback = useCallback(() => {
    setShowChiapasCities(true);
    if (selectedRegion?.region_id === 'CHIS' && !toastShownRef.current.chiapas) {
      toastShownRef.current.chiapas = true;
      toast({
        title: t('chiapasHeatmapTitle'),
        description: t('chiapasHeatmapDescription') || 'Showing church presence heatmap for Chiapas region',
        duration: 5000,
      });
    }
  }, [selectedRegion, toast, t]);

  const onHidalgoEnterCallback = useCallback(() => {
    setShowHidalgoCities(true);
    if (selectedRegion?.region_id === 'HGO' && !toastShownRef.current.hidalgo) {
      toastShownRef.current.hidalgo = true;
      toast({
        title: t('hidalgoHeatmapTitle') || 'Hidalgo Region',
        description: t('hidalgoHeatmapDescription') || 'Showing church presence heatmap for Hidalgo region',
        duration: 5000,
      });
    }
  }, [selectedRegion, toast, t]);

  const { isInChiapas, isInHidalgo, isGlobalView, showGlobalView, showMexicoView } = useMapNavigation(
    map, 
    mapLoaded, 
    selectedRegion, 
    selectedCity, 
    setMapZoomLevel,
    onChiapasEnterCallback,
    onHidalgoEnterCallback,
    wasInMexicoView
  );

  useEffect(() => {
    setIsMexicoView(!isGlobalView && (!selectedRegion || wasInMexicoView));
  }, [isGlobalView, selectedRegion, wasInMexicoView]);

  useEffect(() => {
    if (!styleElementRef.current) {
      const styleElement = document.createElement('style');
      styleElement.id = 'mexico-map-permanent-styles';
      styleElement.textContent = `
        /* Force Mexico region markers to stay visible */
        body .mapboxgl-marker.mexico-region-funding {
          visibility: visible !important;
          opacity: 1 !important;
          z-index: 9999 !important;
          display: block !important;
          pointer-events: none !important;
          position: absolute !important;
        }
        
        /* Hide city markers in Mexico view */
        body .mexico-view .city-marker {
          display: none !important;
        }
      `;
      
      document.head.appendChild(styleElement);
      styleElementRef.current = styleElement;
      
      return () => {
        if (styleElementRef.current) {
          document.head.removeChild(styleElementRef.current);
          styleElementRef.current = null;
        }
      };
    }
  }, []);

  useEffect(() => {
    return () => {
      toastShownRef.current = { chiapas: false, hidalgo: false };
      
      if (styleElementRef.current) {
        document.head.removeChild(styleElementRef.current);
        styleElementRef.current = null;
      }
    };
  }, [map]);

  useEffect(() => {
    if (isInChiapas) {
      setShowChiapasCities(true);
    } else if (!selectedRegion || selectedRegion.region_id !== 'CHIS') {
      setShowChiapasCities(false);
    }
    if (isInHidalgo) {
      setShowHidalgoCities(true);
    } else if (!selectedRegion || selectedRegion.region_id !== 'HGO') {
      setShowHidalgoCities(false);
    }
  }, [isInChiapas, isInHidalgo, selectedRegion]);
  
  useEffect(() => {
    setShowGlobalRegions(isGlobalView);
    setIsMexicoView(!isGlobalView && (!selectedRegion || wasInMexicoView));
    
    if (!isGlobalView && (!selectedRegion || wasInMexicoView)) {
      document.body.classList.add('mexico-country-view');
    } else {
      document.body.classList.remove('mexico-country-view');
    }
  }, [isGlobalView, selectedRegion, wasInMexicoView]);

  useEffect(() => {
    if (map.current && mapLoaded) {
      console.log("MexicoMap: Map loaded, checking if Mapbox is set up correctly");
      const hasToken = verifyMapboxSetup();
      
      if (!hasToken) {
        console.error("Mapbox token is not set!");
        toast({
          title: "Map Error",
          description: "Mapbox token is not configured correctly",
          variant: "destructive",
        });
        return;
      }
      
      console.log("MexicoMap: Waiting for map style to load before adding vector layers");
      waitForStyleLoad(map.current).then(() => {
        console.log("MexicoMap: Map style loaded, checking view type");
        if (!showGlobalRegions && isMexicoView) {
          console.log("MexicoMap: In Mexico view, adding vector layers now");
          addVectorLayers(map.current);
          setLayersAdded(true);
          
          toast({
            title: "Map Updated",
            description: "Mexico regions colored by church presence",
            duration: 3000,
          });
        } else {
          console.log("MexicoMap: Not in Mexico view, skipping vector layers");
        }
      });
    }
  }, [map.current, mapLoaded, showGlobalRegions, isMexicoView]);

  useEffect(() => {
    if (map.current && mapLoaded && isMexicoView && !showGlobalRegions && !layersAdded) {
      console.log("MexicoMap: Switched to Mexico view, adding vector layers");
      waitForStyleLoad(map.current).then(() => {
        addVectorLayers(map.current);
        setLayersAdded(true);
      });
    }
  }, [isMexicoView, showGlobalRegions, map.current, mapLoaded, layersAdded]);
  
  useEffect(() => {
    return () => {
      setLayersAdded(false);
    };
  }, []);

  useMexicoMapHandles({
    map,
    isMobile,
    setMapZoomLevel,
    setShowChiapasCities,
    setShowHidalgoCities,
    toastShownRef,
    formatCurrencyShort,
    ref,
    selectedRegion,
    setShowGlobalRegions,
    showMexicoView,
    setIsMexicoView
  });

  const { handleInternalRegionClick, handleInternalCityClick, getCitiesToShow } = useMexicoMapSelection({
    selectedRegion,
    setShowChiapasCities,
    setShowHidalgoCities,
    toastShownRef,
    onRegionClick,
    onCityClick,
    chiapas_cities,
    hidalgo_cities
  });

  useEffect(() => {
    if (map.current && mapLoaded) {
      const specialRegionsStyle = document.createElement('style');
      specialRegionsStyle.textContent = `
        .mapboxgl-canvas-container .region-boundary-chiapas,
        .mapboxgl-canvas-container .region-boundary-hidalgo {
          visibility: visible !important;
          opacity: 1 !important;
        }
      `;
      document.head.appendChild(specialRegionsStyle);
      
      return () => {
        document.head.removeChild(specialRegionsStyle);
      };
    }
  }, [map, mapLoaded]);

  useEffect(() => {
    const container = map.current?.getContainer();
    if (container) {
      if (selectedRegion) {
        container.classList.add('region-view');
        container.classList.remove('mexico-view');
      } else if (isMexicoView) {
        container.classList.add('mexico-view');
        container.classList.remove('region-view');
      } else {
        container.classList.remove('mexico-view');
        container.classList.remove('region-view');
      }
    }

    return () => {
      if (container) {
        container.classList.remove('region-view');
        container.classList.remove('mexico-view');
      }
    };
  }, [selectedRegion, isMexicoView, map.current]);

  useEffect(() => {
    const container = mapContainer.current;
    if (container) {
      if (isMexicoView) {
        container.classList.add('mexico-view-container');
      } else {
        container.classList.remove('mexico-view-container');
      }
    }
  }, [isMexicoView]);

  return (
    <LegendFilterProvider>
      <MapContainer 
        mapRef={mapContainer}
        showFunding={props.showFunding} 
        setShowFunding={props.setShowFunding}
      >
        {map.current && mapLoaded && (
          <MapMarkers 
            map={map.current}
            regions={regions}
            cities={getCitiesToShow()}
            filteredRegionIds={filteredRegionIds}
            selectedRegion={selectedRegion}
            selectedCity={selectedCity}
            onRegionClick={handleInternalRegionClick}
            onCityClick={handleInternalCityClick}
            markerRefs={markerRefs}
            mapZoomLevel={mapZoomLevel}
            is3DMode={is3DMode}
            showChiapasCities={showChiapasCities}
            showHidalgoCities={showHidalgoCities}
            showGlobalRegions={showGlobalRegions}
            isMexicoView={isMexicoView}
            showFunding={props.showFunding}
          />
        )}
        {(!isMobile || !selectedRegion) && 
          <MapLegend 
            regions={regions}
            selectedRegion={selectedRegion}
            isGlobalView={isGlobalView}
            isMexicoView={isMexicoView}
          />
        }
      </MapContainer>
    </LegendFilterProvider>
  );
});

MexicoMap.displayName = 'MexicoMap';
export default MexicoMap;
